import React, { useState, useRef } from "react";
import Papa from "papaparse";
import {
  downloadAsset,
  getAssetContentAsText,
  getAssetType,
} from "~/lib/network/assets";

interface AssetItemProps {
  title: string;
  description: string;
  filename: string;
  sessionId: string;
}

const AssetItem = ({
  title,
  description,
  filename,
  sessionId,
}: AssetItemProps) => {
  const assetType = getAssetType(filename);
  const [isExpanded, setIsExpanded] = useState(assetType === "html");
  const [content, setContent] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const modalRef = useRef<HTMLDialogElement>(null);

  // Auto-fetch content for HTML files on mount
  React.useEffect(() => {
    if (assetType === "html") {
      setIsLoading(true);
      getAssetContentAsText(sessionId, filename)
        .then(setContent)
        .catch((error) => {
          console.error("Failed to fetch asset content:", error);
          setContent("Error loading content");
        })
        .finally(() => setIsLoading(false));
    }
  }, [sessionId, filename, assetType]);

  const handleDownload = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await downloadAsset(sessionId, filename);
    } catch (error) {
      console.error("Failed to download asset:", error);
    }
  };

  const handleFullView = (e: React.MouseEvent) => {
    e.stopPropagation();
    modalRef.current?.showModal();
  };

  const handleCardClick = async (e: React.MouseEvent) => {
    if (assetType === "excel") {
      await handleDownload(e);
      return;
    }

    if (!isExpanded && !content) {
      setIsLoading(true);
      try {
        const assetContent = await getAssetContentAsText(sessionId, filename);
        setContent(assetContent);
      } catch (error) {
        console.error("Failed to fetch asset content:", error);
        setContent("Error loading content");
      } finally {
        setIsLoading(false);
      }
    }

    setIsExpanded(!isExpanded);
  };

  const renderContent = () => {
    if (!content) return null;

    if (assetType === "html") {
      return (
        <div className="w-full max-h-[600px] border rounded-lg overflow-hidden">
          <iframe
            className="w-full h-[600px]"
            srcDoc={content}
            title={`HTML Preview: ${filename}`}
            sandbox="allow-same-origin allow-scripts"
          />
        </div>
      );
    }

    if (assetType === "csv") {
      // Parse CSV properly using PapaParse to handle quoted fields with commas
      const parsed = Papa.parse(content, {
        header: false,
        skipEmptyLines: true,
      });

      const headers = parsed.data[0] as string[] || [];
      const rows = parsed.data.slice(1) as string[][];

      return (
        <div className="w-full max-h-80 overflow-x-auto overflow-y-auto border rounded-lg">
          <table className="table table-zebra table-sm">
            <thead className="sticky top-0 bg-base-200 z-10">
              <tr>
                {headers.map((header, index) => (
                  <th key={index} className="font-semibold bg-base-200 whitespace-nowrap">
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {row.map((cell, cellIndex) => (
                    <td key={cellIndex} className="whitespace-nowrap">{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    return (
      <pre className="bg-base-200 p-4 rounded-lg text-sm overflow-auto max-h-80">
        {content}
      </pre>
    );
  };

  const renderModal = () => (
    <dialog ref={modalRef} className="modal">
      <div className="modal-box w-11/12 max-w-5xl h-5/6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h3 className="font-bold text-lg">{title}</h3>
            <p className="text-sm opacity-70 font-mono">{filename}</p>
          </div>
          <div className="modal-action mt-0">
            <button className="btn btn-sm btn-ghost" onClick={handleDownload}>
              Download
            </button>
            <form method="dialog">
              <button className="btn btn-sm btn-circle btn-ghost">✕</button>
            </form>
          </div>
        </div>
        <div className="w-full h-full">
          {content && (
            <iframe
              className="w-full h-full border rounded-lg"
              srcDoc={content}
              title={`HTML Preview: ${filename}`}
              sandbox="allow-same-origin allow-scripts"
            />
          )}
        </div>
      </div>
      <form method="dialog" className="modal-backdrop">
        <button>close</button>
      </form>
    </dialog>
  );

  return (
    <>
      <div
        className="card bg-base-100 shadow-sm border cursor-pointer"
        onClick={handleCardClick}
      >
        <div className="card-body p-4">
          <div className="card-title justify-between items-start">
            <div className="flex-1">
              <h3 className="text-lg font-semibold">{title}</h3>
              <p className="text-sm opacity-70 mt-1">{description}</p>
              <p className="text-xs font-mono opacity-60 mt-1">{filename}</p>
            </div>
            <div className="card-actions">
              {assetType === "excel" ? (
                <button
                  className="btn btn-sm btn-primary"
                  onClick={handleCardClick}
                >
                  Download
                </button>
              ) : (
                <div className="flex gap-2">
                  {assetType === "html" && (
                    <>
                      <button
                        className="btn btn-sm btn-ghost"
                        onClick={handleFullView}
                      >
                        Full View
                      </button>
                      <button
                        className="btn btn-sm btn-ghost"
                        onClick={handleDownload}
                      >
                        Download
                      </button>
                    </>
                  )}
                  <button
                    className="btn btn-sm btn-outline"
                    onClick={handleCardClick}
                    disabled={isLoading}
                  >
                    {isLoading
                      ? "Loading..."
                      : isExpanded
                      ? "Collapse"
                      : "View"}
                  </button>
                </div>
              )}
            </div>
          </div>

          {assetType !== "excel" && (
            <div className="collapse collapse-arrow w-full">
              <input
                type="checkbox"
                checked={isExpanded}
                onChange={() => setIsExpanded(!isExpanded)}
                className="min-h-0"
              />
              <div className="collapse-content px-0 w-full overflow-auto">
                {isExpanded && renderContent()}
              </div>
            </div>
          )}
        </div>
      </div>
      {renderModal()}
    </>
  );
};

export default AssetItem;
